import { QueryClient, useSuspenseQuery } from "@tanstack/react-query";

import type { PathParamsBase, QueryParamsBase } from "@mpa/toolbox/endpoint/Endpoint";
import type OutgoingRequest from "@mpa/toolbox/endpoint/OutgoingRequest";
import Caster from "@mpa/toolbox/error/Caster";
import { Problem } from "@mpa/toolbox/error/Problem";

import env from "../screens/RootNavigator/services/env";

export const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            retry: (failureCount, error) => {
                if (error instanceof Problem) return false;
                return failureCount < 3;
            },
        },
    },
});
export const errors = new Caster(new Problem("Une erreur est survenue"));
errors.register(Problem, (problem) => problem);
errors.register(Problem.isProblem, (data) => Problem.parse(data));

async function fetchOutgoingRequest<
    PathParams extends PathParamsBase,
    QueryParams extends QueryParamsBase,
    Body,
    Result,
    Deserialized,
>(req: OutgoingRequest<PathParams, QueryParams, Body, Result, Deserialized>) {
    let path = `${env.SERVICES_ORIGIN}/api${req.getPath()}`;
    if (req.queryParams) {
        path += "?" + new URLSearchParams(req.queryParams).toString();
    }
    const body = req.body ? JSON.stringify(req.body) : null;
    const init: RequestInit = {
        method: req.endpoint.getMethod(),
        credentials: "include",
        headers: { "Content-Type": "application/json" },
    };
    if (body) init.body = body;
    const output = await fetch(path, init);
    const json = await output.json();
    if (!output.ok) throw errors.cast(json);
    return req.parseResult(json);
}

export function useOutgoingRequest<
    PathParams extends PathParamsBase,
    QueryParams extends QueryParamsBase,
    Body,
    Result,
    Deserialized,
>(
    req: OutgoingRequest<PathParams, QueryParams, Body, Result, Deserialized>,
    queryKeySuffix: unknown[] = [],
) {
    const result = useSuspenseQuery<Deserialized>({
        queryKey: [req.getHash(), ...queryKeySuffix],
        queryFn: () => fetchOutgoingRequest(req),
    });
    return result.data;
}

export function refreshOutgoingRequest<
    PathParams extends PathParamsBase,
    QueryParams extends QueryParamsBase,
    Body,
    Result,
    Deserialized,
>(req: OutgoingRequest<PathParams, QueryParams, Body, Result, Deserialized>) {
    return queryClient.invalidateQueries({ queryKey: [req.getHash()] });
}

export function deleteOutgoingRequest<
    PathParams extends PathParamsBase,
    QueryParams extends QueryParamsBase,
    Body,
    Result,
    Deserialized,
>(req: OutgoingRequest<PathParams, QueryParams, Body, Result, Deserialized>) {
    return queryClient.invalidateQueries({ queryKey: [req.getHash()] });
}

export async function callOutgoingRequest<
    PathParams extends PathParamsBase,
    QueryParams extends QueryParamsBase,
    Body,
    Result,
    Deserialized,
>(req: OutgoingRequest<PathParams, QueryParams, Body, Result, Deserialized>) {
    return await fetchOutgoingRequest(req);
}
